// ─────────────────────────────────────────────────────────────────────────────
// The month grid behind the date picker. Pure — no DOM, no clock of its own.
//
// ⚠ EVERY DATE HERE IS A LOCAL CALENDAR DATE, written as `YYYY-MM-DD` and never
// as a `Date` crossing a boundary. `due.ts` states the reason at length: a
// date-only string is parsed as UTC by spec, so a calendar built out of instants
// puts the month's first day in the previous month west of Greenwich.
// ─────────────────────────────────────────────────────────────────────────────

import { currentLanguage, dateLocale, t } from "home/app/mobile/i18n";

/**
 * Monday-first, because the design's grid is and because Switzerland is.
 *
 * ⚠ The ENGLISH headings, kept for compatibility — a screen should call `weekdayLabels()`, which is
 * in the app's language. A constant cannot be: it is evaluated once, at import, before any language
 * is known.
 */
/**
 * The grid's column heads, Monday-first, in the design's two-letter capitals: MO TU … / MO DI ….
 *
 * From `Intl` in `dateLocale()` rather than a translation id per day: the browser already knows every
 * weekday in both languages, and seven ids would be seven chances to drift. 2026-01-05 is a Monday,
 * so the seven days after it are the week in grid order. Two letters, dot dropped, because `Mo.` and
 * `Mon` are both wider than a cell.
 */
export function weekdayLabels(): string[] {
    const format = new Intl.DateTimeFormat(dateLocale(), { weekday: "short" });

    return Array.from({ length: 7 }, (_, i) =>
        format
            .format(new Date(2026, 0, 5 + i))
            .replace(".", "")
            .slice(0, 2)
            .toUpperCase(),
    );
}

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export interface CalendarCell {
    /** `YYYY-MM-DD`. */
    iso: string;
    /** The day of the month, 1..31 — what the cell prints. */
    day: number;
    /** True for the leading and trailing days that belong to a neighbouring month. */
    outside: boolean;
}

/** `YYYY-MM-DD` for a local date. */
export function toISO(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The `YYYY-MM` a date sits in. */
export function monthOf(iso: string): string {
    return /^\d{4}-\d{2}/.exec(String(iso ?? ""))?.[0] ?? "";
}

/** A month key `YYYY-MM` moved by whole months. Rolls the year, both ways. */
export function shiftMonth(month: string, delta: number): string {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return month;

    const [, y, m] = parsed;
    // Day 1 rather than the current day: the 31st of a month shifted into a
    // 30-day one would roll into the next month and skip it entirely.
    const date = new Date(Number(y), Number(m) - 1 + delta, 1);

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * "August 2026" — in the app's language, for the reason `due.ts` gives. English from a fixed table,
 * so it stays byte-identical; German through Intl ("August 2026", "März 2026").
 */
export function monthTitle(month: string): string {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return "";
    if (currentLanguage() === "en") return `${MONTHS[Number(parsed[2]) - 1]} ${parsed[1]}`;

    return new Intl.DateTimeFormat(dateLocale(), { month: "long", year: "numeric" }).format(
        new Date(Number(parsed[1]), Number(parsed[2]) - 1, 1),
    );
}

/**
 * The 42 cells of a month grid, Monday-first.
 *
 * ⚠ ALWAYS 42, never 35 or a ragged tail. A grid that changes height as the
 * months are paged makes the buttons under it move while somebody is reaching
 * for them, and the design draws six rows.
 *
 * The leading and trailing cells are real, selectable dates in the neighbouring
 * months — the design says picking one navigates there — so they carry their own
 * `iso` and are only marked `outside`.
 */
export function monthGrid(month: string): CalendarCell[] {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return [];

    const year = Number(parsed[1]);
    const index = Number(parsed[2]) - 1;
    const first = new Date(year, index, 1);

    // getDay() is Sunday-first; this is the Monday-first offset of the 1st.
    const lead = (first.getDay() + 6) % 7;
    const cells: CalendarCell[] = [];

    for (let i = 0; i < 42; i++) {
        const date = new Date(year, index, 1 - lead + i);
        cells.push({
            iso: toISO(date),
            day: date.getDate(),
            outside: date.getMonth() !== index,
        });
    }

    return cells;
}

export interface QuickDate {
    label: string;
    iso: string;
}

/**
 * The three shortcuts above the grid.
 *
 * ⚠ "Next Monday" is the NEXT one, never today. On a Monday it is seven days
 * away, not zero — a shortcut that resolved to the day you are already on would
 * duplicate "Today" one button along and silently do nothing.
 */
export function quickDates(now: number): QuickDate[] {
    const today = new Date(now);
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const plus = (days: number) => toISO(new Date(base.getFullYear(), base.getMonth(), base.getDate() + days));

    const toMonday = (8 - base.getDay()) % 7 || 7;

    return [
        { label: t("mobileTextDueToday", "Today"), iso: plus(0) },
        { label: t("mobileTextDueTomorrow", "Tomorrow"), iso: plus(1) },
        { label: t("mobileTextNextMonday", "Next Monday"), iso: plus(toMonday) },
    ];
}
